// Pick 6: the customer picks any 6 of the numbers 1–40; the coupon prints 20
// winning numbers, one on each of the product's option prizes, drawn for that
// coupon after the pick. A picked number that is a winning number wins its
// prize × Factor. How many times each prize is won per 100 coupons is set per
// type (WinnerConfig), as for the other products; each winner is one winning
// coupon paying that prize — 40% of them on one of the customer's numbers, 60%
// split across several (100 = 5+5+10+20+30+30), where the option prizes allow.
// Shared by the Products page, the Coupon round and the Transactions viewer.

export const PICK6 = 'pick6';
export const PICK6_LABEL = 'Pick 6 of 40 — match a winning option';
export const PICK6_RULE = 'MATCH ANY OF YOUR NUMBERS TO WIN';
export const PICK6_MAX_OPTIONS = 20;
export const PICK6_BOXES = 40;
export const PICK6_PICKS = 6;
export const PICK6_SECONDS = 20;
// Winners per type per 100 coupons: each winner is one winning coupon.
export const PICK6_MAX_WINNERS = 100;

export const isPick6 = (product) => (product?.matchingMethod || product?.winCondition) === PICK6;

// The same rules PW_ProductWinningOptions_Save enforces, checked first so the
// admin sees every problem at once. `rows` hold the raw prize text;
// `denominations` are the product's (its types'), when it has any.
export const validateOptions = (rows, denominations = []) => {
  const problems = [];
  if (!rows.length) problems.push('Add at least one winning option.');
  if (rows.length > PICK6_MAX_OPTIONS) problems.push(`A Pick 6 product can have at most ${PICK6_MAX_OPTIONS} winning options.`);
  if (rows.some(r => String(r.prize).trim() === '')) problems.push('Every option needs a prize.');
  else if (rows.some(r => !(Number(r.prize) > 0))) problems.push('Each prize must be more than 0.');
  else if (denominations.length && rows.some(r => !denominations.includes(Number(r.prize)))) {
    problems.push('Each prize must be one of the product\'s denominations.');
  }
  return problems;
};

// Winners a linked type has that the coupon can't print: a prize no option
// carries ('missing'). The server refuses to sell such a type, and to save
// options that cause it.
export const winnerProblems = (links, options) => {
  const carried = new Set((options || []).map(o => Number(o.prize)));
  const out = [];
  for (const l of links || []) {
    if (!l.linked) continue;
    for (const [d, c] of Object.entries(l.winnerConfig || {})) {
      if (c > 0 && !carried.has(Number(d))) out.push({ typeId: l.typeId, prize: Number(d), winners: c, kind: 'missing' });
    }
  }
  return out;
};

// A Pick 6 coupon's options come as { number, prize } from the API and as
// { n, p } from a saved sale's snapshot.
export const optionNumber = (o) => o.number ?? o.n;
export const optionPrize = (o) => o.prize ?? o.p;

// Why the server refused to start or sell a Pick 6 round (schedule:false with
// a reason). No reason means there is no schedule — the page's usual dialog.
const REFUSALS = {
  'pick6-no-options': 'This product has no winning options. Set them on the Products page.',
  'pick6-too-many-winners': 'This product\'s winners add up to more than 100 per 100 coupons. Fix them on the Products page.',
  'pick6-missing-prize': 'This product has winners for a prize that is not on any winning option. Fix it on the Products page.',
  'pick6-too-many-options': 'This product has too many winning options. Fix them on the Products page.',
  'pick6-bad-batch': 'This product must run in batches of 100 coupons. Save it again on the Products page.',
  'pick6-unsupported-client': 'This page is out of date. Reload it to sell this product.',
  'pick6-bad-picks': 'Pick 6 different numbers from 1 to 40, then press Confirm.',
};
export const pick6Refusal = (reason) =>
  REFUSALS[reason] ? { title: 'Cannot Sell This Product', text: REFUSALS[reason] } : null;
